import type { Header, SortDirection } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import {
  type AriaAttributes,
  type ForwardedRef,
  forwardRef,
  type HTMLProps,
  type KeyboardEventHandler,
} from 'react';

import { Icon } from '~/chords/Icon';
import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

import { useTableContext } from '../../hooks/useTableContext';

interface TableHeadCellProps<TData, TValue>
  extends Omit<HTMLProps<HTMLTableCellElement>, 'className'> {
  header: Header<TData, TValue>;
  sortDirection?: SortDirection;
}

const tableHeadCellVariants = variants({
  base: cn(
    'p-md',
    'text-body-sm',
    'font-normal',
    'border-none',
    'transition-all',
    'duration-300',
  ),
  variants: {
    isSortable: {
      true: cn('hover:text-strong', 'cursor-pointer'),
    },
    size: {
      xs: cn('p-xs'),
      sm: cn('px-sm py-xs'),
      md: cn('px-md py-sm'),
      lg: cn('px-lg py-md'),
      xl: cn('px-xl py-lg'),
    },
  },
});

function TableHeadCellInner<TData, TValue>(
  { header, ...props }: TableHeadCellProps<TData, TValue>,
  ref: ForwardedRef<HTMLTableCellElement>,
) {
  const { size } = useTableContext();
  const isSortable = header.column.getCanSort();
  const sortDirection = header.column.getIsSorted();

  const toggleSort = (e: unknown) => {
    if (isSortable) {
      header.column.getToggleSortingHandler()?.(e);
    }
  };

  const onKeyDown: KeyboardEventHandler = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      toggleSort(e);
    }
  };

  let sortLabel: AriaAttributes['aria-sort'] = 'none';

  if (sortDirection === 'asc') {
    sortLabel = 'ascending';
  } else if (sortDirection === 'desc') {
    sortLabel = 'descending';
  }

  return (
    <th
      {...props}
      ref={ref}
      className={cn(tableHeadCellVariants({ isSortable, size }))}
      style={{
        width: `calc(var(--header-${header.id}-size) * 1px)`,
      }}
      tabIndex={isSortable ? 0 : undefined}
      aria-sort={sortLabel}
      onClick={toggleSort}
      onKeyDown={onKeyDown}
    >
      <div className="flex items-center gap-sm">
        {flexRender(header.column.columnDef.header, header.getContext())}
        {isSortable && !sortDirection ? (
          <Icon type="sort" variant="regular" aria-label="not sorted" />
        ) : null}
        {sortDirection ? (
          <div
            className="flex"
            role="img"
            aria-label={`sorted ${sortDirection === 'asc' ? 'ascending' : 'descending'}`}
          >
            <Icon
              aria-hidden
              type="sort-up"
              variant={sortDirection === 'asc' ? 'solid' : 'regular'}
            />
            <Icon
              aria-hidden
              type="sort-down"
              variant={sortDirection === 'desc' ? 'solid' : 'regular'}
              dangerouslyOverrideClassName={{
                __className: '-translate-x-full',
              }}
            />
          </div>
        ) : null}
      </div>
    </th>
  );
}

TableHeadCellInner.displayName = 'TableHeadCell';

export const TableHeadCell = forwardRef(TableHeadCellInner) as <TData, TValue>(
  props: TableHeadCellProps<TData, TValue> & {
    ref?: React.ForwardedRef<HTMLTableCellElement>;
  },
) => ReturnType<typeof TableHeadCellInner>;
